import React, { useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/** Auto-fix Leaflet when the container (or viewport) resizes */
function InvalidateOnResize() {
  const map = useMap();
  React.useEffect(() => {
    const kick = () => map.invalidateSize({ animate: false });
    const id = setTimeout(kick, 0);
    const ro = new ResizeObserver(kick);
    ro.observe(map.getContainer());
    window.addEventListener("resize", kick);
    window.addEventListener("orientationchange", kick);
    return () => {
      clearTimeout(id);
      ro.disconnect();
      window.removeEventListener("resize", kick);
      window.removeEventListener("orientationchange", kick);
    };
  }, [map]);
  return null;
}

export const LiveTracking = () => {
  // Example coordinates (change to your live positions)
  const start = [5.012, 7.925]; // rider
  const end = [5.023, 7.952];   // destination
  const route = [
    [5.012, 7.925],
    [5.015, 7.932],
    [5.018, 7.940],
    [5.020, 7.948],
    [5.023, 7.952],
  ];

  // Gold round markers 
  const goldMarker = useMemo(
    () =>
      L.divIcon({
        className: "",
        html: `<div style="
          width:26px;height:26px;border-radius:9999px;
          background:#f7c945;border:3px solid #a6842a;
          box-shadow:0 0 0 2px rgba(0,0,0,0.12) inset;"></div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      }),
    []
  );

  const center = useMemo(
    () => [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2],
    []
  );

  return (
    <div className="min-h-screen bg-[#fff8e1] pb-28">
      {/* Map wrapper with explicit height */}
      <div className="mx-auto w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16 pt-[max(env(safe-area-inset-top),24px)] md:pt-8">
        <div className="relative w-full h-[70vh] md:h-[76vh] rounded-2xl overflow-hidden border border-[#5d4037]/20 z-0">
          <MapContainer
            center={center}
            zoom={13}
            scrollWheelZoom
            className="absolute inset-0 h-full w-full z-0"
          >
            <InvalidateOnResize />

            {/* OpenStreetMap tiles */}
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />

            {/* Route line in Sweet-Plate brown */}
            <Polyline positions={route} pathOptions={{ color: "#5d4037", weight: 6 }} />
            {/* Start / End markers */}
            <Marker position={start} icon={goldMarker} />
            <Marker position={end} icon={goldMarker} />
          </MapContainer>
        </div>
      </div>

      {/* Bottom sheet / rider card */}
      <div className="fixed inset-x-0 bottom-0 z-[1000]">
        <div className="mx-auto w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16 pb-[max(env(safe-area-inset-bottom),12px)]">
          <div className="bg-[#5d4037] text-white rounded-t-xl p-4 shadow-[0_-8px_24px_rgba(0,0,0,0.15)]">
            <div className="mx-auto mb-2 w-[120px] h-[6px] rounded-full bg-white/90" />
            <div className="flex items-center gap-3">
              <img
                alt=""
                className="w-9 h-9 rounded-full object-cover"
                src="https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=200&q=80&auto=format&fit=crop"
              />
              <div className="flex-1">
                <p className="font-extrabold leading-tight">Dispatch Assigned</p>
                <p className="text-[13px] leading-tight">Ron Hellion</p>
                <p className="text-[13px] leading-tight">Rider ID: 7632</p>
              </div>

              {/* action buttons */}
              <button
                className="w-10 h-10 rounded-full bg-white/10 border border-white grid place-items-center mr-2"
                aria-label="Chat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M21 15a4 4 0 01-4 4H7l-4 4V7a4 4 0 014-4h10a4 4 0 014 4z"/>
                </svg>
              </button>
              <button
                className="w-10 h-10 rounded-full bg-white/10 border border-white grid place-items-center"
                aria-label="Call"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.86 19.86 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.86 19.86 0 013.08 4.18 2 2 0 015.06 2h3a2 2 0 012 1.72c.12.9.33 1.78.61 2.63a2 2 0 01-.45 2.11L9 9a16 16 0 006 6l.54-.22a2 2 0 012.11.45c.85.28 1.73.49 2.63.61A2 2 0 0122 16.92z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
